import ODAS from '@/utils/api/odas';
import ODRS from '@/utils/api/odrs';
import { resourceHelpers } from '@/utils/helpers';

// Used by the regex below to safely match a complete Ruby string even if it contains escaped quotes.
const QUOTED_STRING = '"(?:[^"\\\\]|\\\\.)*"';

// Matches a property value that is really a stringified Ruby hash, e.g.
// `{"name"=>"ada-accessible", "value"=>"Yes", "label"=>"ADA Accessible"}`
const LABELLED_PROPERTY_VALUE = new RegExp(
  `^\\{"name"=>(?:${QUOTED_STRING}|nil), "value"=>(${QUOTED_STRING}|nil), "label"=>(?:${QUOTED_STRING}|nil)\\}$`
);

// Decodes via JSON parse any property that needs it, so that what gets submitted to ODRS is a plain
// string. Returns null -- meaning "leave it alone" -- for an unset value or anything JSON cannot read.
const decodePropertyValue = (captured) => {
  if (captured === 'nil') return null;
  try {
    const decoded = JSON.parse(captured);
    return typeof decoded === 'string' ? decoded : null;
  } catch (error) {
    return null;
  }
};

// Reduces a property to the plain string ODRS expects, unwrapping it out of an object, a
// stringified hash, or any nesting of the two. Anything that does not decode is passed through
// unchanged rather than guessed at.
const propertyValue = (property) => {
  let value = property !== null && typeof property === 'object' ? property.value : property;
  let match;
  while (typeof value === 'string' && (match = LABELLED_PROPERTY_VALUE.exec(value))) {
    const unwrapped = decodePropertyValue(match[1]);
    if (unwrapped === null) break;
    value = unwrapped;
  }
  return value;
};

const flattenProperties = (properties) => {
  if (!properties || typeof properties !== 'object') return properties;
  return Object.entries(properties).reduce((flattened, [name, property]) => {
    if (name !== 'translations') flattened[name] = propertyValue(property);
    return flattened;
  }, {});
};

// Builds the content to submit by re-fetching the resource, matching old Angular flows. ODRS applies
// the content as a whole on approval -- every field assigned, and associations missing from it
// deleted -- so it has to be the canonical English payload with properties as plain key/value
// pairs. Refetching it here at submission time ensures we send ODRS the expected data, unmodified by anything client-side.
const submissionContentFor = async (resource) => {
  const collection = resourceHelpers.collectionName(resource?.resource_type);
  const response = collection && resource?.id ? await ODRS.getResourceById(collection, resource.id) : null;
  // if the resource could not be fetched, throw an Error which gets handled by the caller to display a toast
  if (!response?.data?.id) {
    throw new Error('Could not load the resource to submit');
  }
  return { ...response.data, properties: flattenProperties(response.data.properties) };
};

// Submits a statement *about* a resource -- it was verified, it is unavailable, it should be
// deleted -- rather than an edit to it. The resource content is re-fetched, so approving one of
// these leaves every field exactly as ODRS already has it.
const submitUnchangedResource = async ({ resource, submissionType, submissionSource = null, deletionInfoHash = null }) => {
  const params = {
    [resource?.resource_type?.toLowerCase()]: await submissionContentFor(resource),
    submission_type: submissionType
  };
  if (submissionSource) {
    params.submission_source = submissionSource;
  }
  if (deletionInfoHash) {
    params.deletion_report_details = deletionInfoHash;
  }
  const url = `/api/${resourceHelpers.collectionName(resource?.resource_type)}/${resource.id}`;

  return ODAS.put(url, params);
};

const Submission = {
  submitBadInfoSubmission: async (badInfoHash, resource, submissionSource) => {
    try {
      const params = {
        id: resource?.id,
        resource_type: resource?.resource_type.toLowerCase(),
        submission_type: 'bad_info_report',
        bad_report_details: {
          report_more_information: badInfoHash?.report_more_information,
          status_bad_contact_or_location: badInfoHash?.status_bad_contact_or_location,
          status_inaccurate: badInfoHash?.status_inaccurate,
          status_location_closed: badInfoHash?.status_location_closed
        }
      };
      params[resource?.resource_type?.toLowerCase()] = resource;
      if (submissionSource) {
        params.submission_source = submissionSource;
      }

      let path = '';
      if (resource?.resource_type?.toLowerCase() === 'opportunity') {
        path = '/api/opportunities/bad_info_report';
      } else if (resource?.resource_type?.toLowerCase() === 'organization') {
        path = '/api/organizations/bad_info_report';
      }

      const response = await ODAS.post(path, params);
      return response;
    } catch (error) {
      throw error;
    }
  },
  // Affirms that everything on the page is currently accurate. Submits the unchanged contant for review in Admin panel.
  verifyResource: async ({ resource, submissionSource = null }) =>
    submitUnchangedResource({ resource, submissionType: 'verify_information', submissionSource }),

  // `available` is the state being asserted, so marking a currently-available resource as
  // unavailable passes available: false.
  markResourceAvailability: async ({ resource, available }) =>
    submitUnchangedResource({ resource, submissionType: available ? 'marked_available' : 'marked_unavailable' }),

  // The deletion details are submitted alongside the unchanged content for review in Admin panel.
  requestResourceDeletion: async ({ resource, deletionInfoHash = null, submissionSource = null }) =>
    submitUnchangedResource({ resource, submissionType: 'delete_information', submissionSource, deletionInfoHash })
};

export default Submission;
